import React, { useEffect, useState } from 'react';
import { MediaItem } from '../../../api/structs/media';
import MediaThumbImage from '../MediaThumbImage';
import { getMediaFileUrl, getMediaThumbUrl } from './MediaEditorSection.helpers';

interface SceneVideoPosterProps {
  video: MediaItem;
  /** Scene still painted as the poster; falls back to a video first-frame. */
  posterImage?: MediaItem;
  /**
   * Absolutely-positioned controls laid over the poster (the takes ‹ ›
   * overlay). Rendered as a SIBLING of the play button inside the relative
   * frame — nesting buttons is invalid HTML, and a click on an overlaid
   * control must not also start playback. Gone once the player mounts: an
   * active `<video controls>` owns its surface.
   */
  overlay?: React.ReactNode;
}

/**
 * The scene's playable video as a poster with a play glyph, swapping to a real
 * player on click. Extracted from `MediaEditorSceneDetailPanel` — it is the
 * scene's SINGLE primary media block now that the still is the video's poster
 * frame rather than a separate image rendered above it.
 *
 * The `<video>` element is only mounted after an explicit click. An idle player
 * with preload="none" paints as a large black box (no poster available for
 * ai_video items — the store only derives thumbnails for images), so until
 * then the slot shows the scene still as a compact poster instead.
 */
const SceneVideoPoster: React.FC<SceneVideoPosterProps> = ({ video, posterImage, overlay }) => {
  const [videoActive, setVideoActive] = useState(false);
  useEffect(() => {
    setVideoActive(false);
  }, [video.id]);

  // Both the player and its poster read the SAME versioned URL, so a regenerate
  // moves them together instead of leaving one of them on the old bytes.
  const sceneVideoUrl = getMediaFileUrl(video);

  // The preview box follows the clip's own ratio — a 9:16 video gets a
  // portrait frame instead of being cropped into a landscape strip. Portrait
  // frames are height-capped and centred so the panel keeps its shape.
  const videoSpec = (video.output_spec ?? {}) as Record<string, any>;
  const videoWidth = Number(videoSpec.width) || Number(video.metadata?.width) || 0;
  const videoHeight = Number(videoSpec.height) || Number(video.metadata?.height) || 0;
  const videoAspect = videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : 16 / 9;
  const videoIsPortrait = videoAspect < 1;

  if (videoActive) {
    return (
      <video
        // Re-key on the versioned src: a regenerate reuses the media
        // id (same file_url, cacheable response), so without this the
        // element keeps the bytes it already loaded.
        key={sceneVideoUrl}
        controls
        autoPlay
        preload="metadata"
        src={sceneVideoUrl}
        className={`rounded border border-white/5 ${
          videoIsPortrait ? 'mx-auto block max-h-[28rem] w-auto' : 'w-full'
        }`}
      />
    );
  }

  // The sizing lives on a wrapper div, not the play button: the button fills
  // it and the `overlay` mounts beside the button — see the prop's note on why
  // overlaid controls must be siblings.
  return (
    <div
      style={{ aspectRatio: String(videoAspect) }}
      className={`relative overflow-hidden rounded border border-white/5 ${
        videoIsPortrait ? 'mx-auto h-[26rem] w-auto' : 'w-full'
      }`}
    >
      <button
        type="button"
        onClick={() => setVideoActive(true)}
        className="group absolute inset-0 block h-full w-full"
        aria-label="Play video"
        data-testid="scene-video-poster"
      >
        <MediaThumbImage
          thumbUrl={posterImage ? getMediaThumbUrl(posterImage) : ''}
          originalUrl={getMediaFileUrl(posterImage)}
          videoUrl={sceneVideoUrl}
          alt="Video preview"
          className="w-full h-full"
          imgClassName="w-full h-full object-cover"
        />
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/15 transition-colors">
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-black/60 border border-white/30 text-white">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 translate-x-[1px]">
              <path d="M8 5.14v13.72L19 12 8 5.14Z" />
            </svg>
          </span>
        </span>
      </button>
      {overlay}
    </div>
  );
};

export default SceneVideoPoster;
